/**
 * Admin Shell Context — Phase 3 Architecture
 *
 * Provides role-based context that adapts the admin shell's
 * navigation, branding, and capabilities based on the authenticated
 * user's role (platform_admin vs agency_admin).
 *
 * Both PlatformLayout and ParagonLayout can consume this context
 * to share cross-cutting concerns without coupling their implementations.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type AdminRole = 'platform_admin' | 'agency_admin' | 'unknown'

interface AdminShellState {
  /** Current user's admin role */
  role: AdminRole
  /** User email for display */
  email: string | null
  /** Whether the user has platform admin access */
  hasPlatformAccess: boolean
  /** Whether the user has paragon agency access */
  hasParagonAccess: boolean
  /** Quick switch URLs */
  switchTargets: { label: string; path: string; icon: string }[]
  /** Loading state */
  isLoading: boolean
}

const AdminShellContext = createContext<AdminShellState>({
  role: 'unknown',
  email: null,
  hasPlatformAccess: false,
  hasParagonAccess: false,
  switchTargets: [],
  isLoading: true,
})

// eslint-disable-next-line react-refresh/only-export-components -- context file exports both provider and hook (standard pattern)
export function useAdminShell() {
  return useContext(AdminShellContext)
}

function useCurrentUser() {
  return useQuery({
    queryKey: ['current-user-session'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      return user
    },
    staleTime: 5 * 60_000,
  })
}

function useUserRoles(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-roles', userId],
    queryFn: async () => {
      if (!userId) return []
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
      if (error) return []
      return (data ?? []).map(r => r.role)
    },
    enabled: !!userId,
    staleTime: 5 * 60_000,
  })
}

export function AdminShellProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const { data: roles = [], isLoading: rolesLoading } = useUserRoles(user?.id)

  const state = useMemo<AdminShellState>(() => {
    // The user_roles table stores 'admin' as the role value
    // Platform admins are also in the platform_admins table
    const isAdmin = roles.includes('admin')
    const isPlatformAdmin = isAdmin // Platform admins always have all access
    const isAgencyAdmin = isAdmin // Agency access is also granted to admins

    const role: AdminRole = isPlatformAdmin ? 'platform_admin' : isAgencyAdmin ? 'agency_admin' : 'unknown'

    const switchTargets: AdminShellState['switchTargets'] = []
    if (isPlatformAdmin) {
      switchTargets.push({ label: 'Platform Admin', path: '/platform', icon: 'admin_panel_settings' })
    }
    if (isAgencyAdmin || isPlatformAdmin) {
      switchTargets.push({ label: 'Paragon Agency', path: '/paragon/admin/fleet', icon: 'satellite_alt' })
    }

    return {
      role,
      email: user?.email ?? null,
      hasPlatformAccess: isPlatformAdmin,
      hasParagonAccess: isAgencyAdmin || isPlatformAdmin,
      switchTargets,
      isLoading: userLoading || rolesLoading,
    }
  }, [user, roles, userLoading, rolesLoading])

  return (
    <AdminShellContext.Provider value={state}>
      {children}
    </AdminShellContext.Provider>
  )
}
